import { LitElement, css, html, svg } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fluxHuman } from '../lib/utils.js';

@customElement('flux-graph')
export class FluxGraph extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 150px;
    }
    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
    path {
      fill: none;
      stroke: url(#line-gradient);
      stroke-width: 3px;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .zero-line {
      stroke: var(--sl-color-neutral-300);
      stroke-width: 1px;
      stroke-dasharray: 4;
    }
    .area {
      fill: url(#line-gradient);
      opacity: 0.1;
      stroke: none;
    }
    text {
      font-size: 12px;
      font-weight: bold;
      font-family: var(--sl-font-sans);
      fill: var(--sl-color-neutral-500);
    }
  `;

  @property({ type: Array }) history: any[] = [];
  @state() private width = 0;
  @state() private height = 0;

  private resizeObserver: ResizeObserver | undefined;
  private paddingY = 30;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.width = width;
        this.height = height;
      }
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
  }

  get graphData() {
    if (!this.history || this.history.length < 2 || this.width === 0 || this.height === 0) return null;

    const values = this.history.map(h => h.flux);
    const minFlux = Math.min(0, ...values);
    const maxFlux = Math.max(0, ...values);
    const fluxRange = maxFlux - minFlux || 1;

    const dates = this.history.map(h => h.date.getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const dateRange = maxDate - minDate || 1;

    const graphHeight = this.height - (this.paddingY * 2);

    const getX = (t: number) => {
      return ((t - minDate) / dateRange) * this.width;
    };

    const getY = (f: number) => {
      const normalized = (f - minFlux) / fluxRange;
      return (this.paddingY + graphHeight) - (normalized * graphHeight);
    };

    const points = this.history.map(point => ({
      x: getX(point.date.getTime()),
      y: getY(point.flux),
      original: point
    }));

    const pathData = points.map((p, index) =>
      `${index === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    const zeroY = getY(0);
    const zeroOffset = zeroY / this.height;

    const areaPathData = `
      ${pathData}
      L ${points[points.length - 1].x} ${zeroY}
      L ${points[0].x} ${zeroY}
      Z
    `;

    const sortedByFlux = [...points].sort((a, b) => a.original.flux - b.original.flux);
    const minPoint = sortedByFlux[0];
    const maxPoint = sortedByFlux[sortedByFlux.length - 1];

    return {
      zeroY,
      zeroOffset,
      pathData,
      areaPathData,
      minPoint,
      maxPoint
    };
  }

  getAnchor(x: number) {
    if (x < 50) return 'start';
    if (x > this.width - 50) return 'end';
    return 'middle';
  }

  renderLabel(point: any, isMax: boolean) {
    return svg`
      <text
        x="${point.x}"
        y="${point.y + (isMax ? -10 : 20)}"
        text-anchor="${this.getAnchor(point.x)}"
      >
        ${fluxHuman(point.original.flux)}
      </text>
    `;
  }

  render() {
    const data = this.graphData;
    if (!data) return html``;

    const { zeroY, zeroOffset, pathData, areaPathData, minPoint, maxPoint } = data;

    return svg`
      <svg viewBox="0 0 ${this.width} ${this.height}">
        <defs>
          <linearGradient id="line-gradient" x1="0" x2="0" y1="0" y2="${this.height}" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="var(--sl-color-success-500)" />
            <stop offset="${zeroOffset}" stop-color="var(--sl-color-success-500)" />
            <stop offset="${zeroOffset}" stop-color="var(--sl-color-danger-500)" />
            <stop offset="1" stop-color="var(--sl-color-danger-500)" />
          </linearGradient>
        </defs>
        <path class="area" d="${areaPathData}" />
        <line x1="0" y1="${zeroY}" x2="${this.width}" y2="${zeroY}" class="zero-line" />
        <path d="${pathData}" />

        ${this.renderLabel(maxPoint, true)}
        ${minPoint !== maxPoint ? this.renderLabel(minPoint, false) : ''}
      </svg>
    `;
  }
}
